import React, { useState } from 'react';
import { formatFinanceCompact, formatFinanceCurrency, formatFinancePercent } from '../pendingInvoiceCost';
import { resolveDonutPath, type DonutNode } from './incomeStatement';

// Inline-SVG drill-down donut: at most `DONUT_MAX_SLICES` slices (the rest
// folded into a drillable "Pozostałe (n)" node by `foldSlices`), breadcrumbs
// with a "Wstecz" pill, click-the-ring-centre to go back one level, and an
// entry animation on `.pnl-donut__ring` that CSS disables under
// prefers-reduced-motion (frontend/src/pages/Profitability.css). Escape is
// handled by the workspace, not here, so one listener covers both donuts.
// See modules/reports/DESIGN.md "Drill-down donut".

const RING_RADIUS = 54;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const RING_GAP = 2;
const SERIES_COUNT = 6;

interface DrillDonutProps {
  root: DonutNode | null;
  path: string[];
  onPathChange: (path: string[]) => void;
  onLeafSelect?: (node: DonutNode) => void;
  centerLabelRoot: string;
  centerLabelDrilled: string;
  lossLabel: string;
  rootTip?: string;
  backLabel: string;
  backHintLabel: string;
  emptyMessage: string;
  ariaLabel: string;
  language: string;
  testId: string;
}

const DrillDonut: React.FC<DrillDonutProps> = ({
  root,
  path,
  onPathChange,
  onLeafSelect,
  centerLabelRoot,
  centerLabelDrilled,
  lossLabel,
  rootTip,
  backLabel,
  backHintLabel,
  emptyMessage,
  ariaLabel,
  language,
  testId,
}) => {
  const [hotIndex, setHotIndex] = useState<number | null>(null);
  const [coreHover, setCoreHover] = useState(false);

  if (!root) {
    return <p className="profitability-income-empty" data-testid={testId}>{emptyMessage}</p>;
  }

  const resolved = resolveDonutPath(root, path);
  if (!resolved.valid && path.length > 0) {
    // The drilled-into node no longer exists (data reloaded under an open
    // level, e.g. a period/basis change) — resync to root instead of a dead end.
    onPathChange([]);
  }
  const { chain, current } = resolved;
  const items = current.children || [];
  const drilled = chain.length > 1;
  const total = items.reduce((sum, item) => (
    (item.value ?? 0) > 0 ? sum + (item.value as number) : sum
  ), 0);

  function goBack() {
    onPathChange(path.slice(0, -1));
  }

  function goToDepth(depth: number) {
    onPathChange(path.slice(0, depth));
  }

  function pick(item: DonutNode) {
    if (item.children) {
      onPathChange([...path, item.key]);
    } else if (item.customerKey && onLeafSelect) {
      onLeafSelect(item);
    }
  }

  function isInteractive(item: DonutNode): boolean {
    return Boolean(item.children) || Boolean(item.customerKey);
  }

  let offset = 0;
  const segments = items.map((item, index) => {
    const value = item.value ?? 0;
    if (value <= 0) return null;
    const length = total > 0 ? (value / total) * RING_CIRCUMFERENCE : 0;
    const dash = Math.max(length - RING_GAP, 0);
    const dashOffset = RING_CIRCUMFERENCE / 4 - offset;
    offset += length;
    const percent = total > 0 ? (value / total) * 100 : 0;
    const interactive = isInteractive(item);
    return (
      <circle
        key={item.key}
        className={`pnl-donut__seg ${interactive ? 'is-drill' : ''} ${hotIndex === index ? 'is-hot' : ''}`}
        style={{ ['--pnl-seg' as string]: `var(--pnl-series-${(index % SERIES_COUNT) + 1})` }}
        cx={70}
        cy={70}
        r={RING_RADIUS}
        strokeDasharray={`${dash} ${RING_CIRCUMFERENCE - dash}`}
        strokeDashoffset={dashOffset}
        tabIndex={interactive ? 0 : -1}
        role={interactive ? 'button' : undefined}
        onMouseEnter={() => setHotIndex(index)}
        onMouseLeave={() => setHotIndex(null)}
        onClick={() => pick(item)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            pick(item);
          }
        }}
      >
        <title>{`${item.name}: ${formatFinanceCurrency(item.value)} (${formatFinancePercent(percent, language)})`}</title>
      </circle>
    );
  });

  const hotItem = hotIndex != null ? items[hotIndex] ?? null : null;
  const hotPercent = hotItem && total > 0 ? ((hotItem.value ?? 0) / total) * 100 : 0;
  // Short in the ring („2,2 mln zł”), the exact amount in its title.
  const centerAmount = hotItem ? hotItem.value : total;
  const centerValue = formatFinanceCompact(centerAmount, language);
  const centerTitle = formatFinanceCurrency(centerAmount);
  const centerLabelText = coreHover && drilled
    ? backHintLabel
    : hotItem
      ? ((hotItem.value ?? 0) > 0 ? formatFinancePercent(hotPercent, language) : lossLabel)
      : (drilled ? centerLabelDrilled : centerLabelRoot);

  return (
    <div data-testid={testId}>
      <nav className="pnl-crumbs" aria-label={ariaLabel}>
        {chain.map((node, index) => (
          index === chain.length - 1 ? (
            <span key={node.key} className="pnl-crumbs__here">{node.name}</span>
          ) : (
            <React.Fragment key={node.key}>
              <button type="button" className="pnl-crumbs__link" onClick={() => goToDepth(index)}>{node.name}</button>
              <span className="pnl-crumbs__sep">›</span>
            </React.Fragment>
          )
        ))}
        {drilled ? (
          <button type="button" className="pnl-crumbs__back" onClick={goBack}>
            {backLabel} <kbd>Esc</kbd>
          </button>
        ) : rootTip ? (
          <span className="pnl-crumbs__tip">{rootTip}</span>
        ) : null}
      </nav>
      <div className="pnl-donut-wrap">
        <svg className="pnl-donut" viewBox="0 0 140 140" role="img" aria-label={ariaLabel}>
          <g className="pnl-donut__ring" key={path.join('/')}>
            {segments}
          </g>
          <circle
            className={`pnl-donut__core ${drilled ? '' : 'is-root'}`}
            cx={70}
            cy={70}
            r={40}
            role={drilled ? 'button' : undefined}
            tabIndex={drilled ? 0 : -1}
            onClick={drilled ? goBack : undefined}
            onKeyDown={drilled ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                goBack();
              }
            } : undefined}
            onMouseEnter={() => drilled && setCoreHover(true)}
            onMouseLeave={() => setCoreHover(false)}
          >
            {drilled && <title>{backLabel}</title>}
          </circle>
          <text className="pnl-donut__center-value" x={70} y={68} textAnchor="middle"><title>{centerTitle}</title>{centerValue}</text>
          <text
            className={`pnl-donut__center-label ${coreHover && drilled ? 'is-back' : ''}`}
            x={70}
            y={84}
            textAnchor="middle"
          >
            {centerLabelText}
          </text>
        </svg>
        <ul className="pnl-legend">
          {items.map((item, index) => {
            const isLink = Boolean(item.customerKey);
            const isDrill = Boolean(item.children) && !isLink;
            const value = item.value;
            const percent = value != null && total > 0 ? (value / total) * 100 : null;
            const negative = value != null && value < 0;
            const interactive = isDrill || isLink;
            return (
              <li
                key={item.key}
                className={`pnl-legend__item ${hotIndex === index ? 'is-hot' : ''} ${isDrill ? 'is-drill' : ''} ${isLink ? 'is-link' : ''} ${negative ? 'is-neg' : ''}`}
                style={{ ['--pnl-seg' as string]: `var(--pnl-series-${(index % SERIES_COUNT) + 1})` }}
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                onMouseEnter={() => setHotIndex(index)}
                onMouseLeave={() => setHotIndex(null)}
                onClick={interactive ? () => pick(item) : undefined}
                onKeyDown={interactive ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    pick(item);
                  }
                } : undefined}
              >
                <span className="pnl-legend__swatch" />
                <span className="pnl-legend__name" title={item.name}>{item.name}</span>
                <span className="pnl-legend__value">{formatFinanceCurrency(value)}</span>
                <span className="pnl-legend__percent">
                  {value != null && value > 0 ? formatFinancePercent(percent, language) : '—'}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default DrillDonut;
